import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import axios from 'axios';
import api from '../../../services/api';
import styles from './UserKycPage.module.css';
import UserKycDetails from '../../../components/users/UserKycDetails/UserKycDetails';
import Button from '../../../components/common/Button/Button';
import { REACT_APP_API_BASE_URL } from '../../../apiurl';
import { formatAdminDate } from '../../../utils/datetime';

const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const UserKycPage = () => {
    const [kycList, setKycList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchKycList(currentPage);
    }, [currentPage]);

    useEffect(() => {
        // Check for userId passed via navigation state
        if (location.state?.userId) {
            setSearchTerm(location.state.userId.toString());
            setExpandedUserId(location.state.userId);
        }
    }, [location.state]);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredList(kycList);
        } else {
            const lowerTerm = searchTerm.toLowerCase();
            const filtered = kycList.filter(item =>
                String(item.user_id).includes(lowerTerm) ||
                (item.pan_no && item.pan_no.toLowerCase().includes(lowerTerm)) ||
                (item.adhaar_no && item.adhaar_no.toLowerCase().includes(lowerTerm))
            );
            setFilteredList(filtered);
        }
    }, [searchTerm, kycList]);

    const fetchKycList = async (page) => {
        setLoading(true);
        try {
            const response = await api.get(`/userkyc?page=${page}&limit=${itemsPerPage}`);
            if (response.data && response.status) {
                setKycList(response.data);
                setFilteredList(response.data);

                // Update pagination info
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages);
                }
            } else {
                setError("Failed to fetch KYC records.");
            }
        } catch (err) {
            console.error("Error fetching KYC list:", err);
            setError("Error loading data.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleExpand = (userId) => {
        setExpandedUserId(prev => prev === userId ? null : userId);
    };

    const clearFilter = () => {
        setSearchTerm('');
        setExpandedUserId(null);
        navigate(location.pathname, { replace: true, state: {} });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            setExpandedUserId(null); // Collapse details on page change
        }
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <h2>User KYC Management</h2>
            </div>
            
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Search by User ID, PAN, or Aadhaar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
                {searchTerm && (
                    <button onClick={clearFilter} className={styles.clearButton}>Clear</button>
                )}
            </div>

            {loading ? (
                <div className={styles.loadingState}>Loading KYC Records...</div>
            ) : error ? (
                <div className={styles.errorState}>{error}</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.kycTable}>
                        <thead>
                            <tr>
                                <th>User ID</th>
                                <th>PAN Number</th>
                                <th>Aadhaar Number</th>
                                <th>Progress</th>
                                <th>Status</th>
                                <th>Last Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.length > 0 ? (
                                filteredList.map((item) => (
                                    <React.Fragment key={item.user_id}>
                                        <tr className={`${styles.row} ${expandedUserId === item.user_id ? styles.activeRow : ''}`}>
                                            <td className={styles.userIdCell}>{item.user_id}</td>
                                            <td>{item.pan_no || '-'}</td>
                                            <td>{item.adhaar_no || '-'}</td>
                                            <td>
                                                <div className={styles.percentContainer}>
                                                    <div className={styles.progressBar}>
                                                        <div className={styles.progressFill} style={{ '--progress-width': `${item.kyc_percentage || 0}%` }}></div>
                                                    </div>
                                                    <span className={styles.percentText}>{item.kyc_percentage || 0}%</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={item.kyc_percentage === 100 ? styles.statusComplete : styles.statusPending}>
                                                    {item.kyc_percentage === 100 ? 'Completed' : 'Pending'}
                                                </span>
                                            </td>
                                            <td>{formatAdminDate(item.updated_at, '-')}</td>
                                            <td>
                                                <Button
                                                    size="small"
                                                    variant="secondary"
                                                    onClick={() => handleToggleExpand(item.user_id)}
                                                >
                                                    {expandedUserId === item.user_id ? 'Hide Details' : 'View Details'}
                                                </Button>
                                            </td>
                                        </tr>
                                        {expandedUserId === item.user_id && (
                                            <tr className={styles.detailsRow}>
                                                <td colSpan="7">
                                                    <UserKycDetails userId={item.user_id} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className={styles.noData}>No KYC records found matching your search.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className={styles.imTableFooter}>
                        <span>Showing {filteredList.length} records</span>

                        {totalPages > 1 && (
                            <div className={styles.imPagination}>
                                <button
                                    type="button"
                                    className={styles.imPageBtn}
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                >
                                    {"<"}
                                </button>
                                
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                                    if (
                                        p === 1 ||
                                        p === totalPages ||
                                        (p >= currentPage - 1 && p <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                type="button"
                                                key={p}
                                                className={`${styles.imPageBtn} ${currentPage === p ? styles.active : ""}`}
                                                onClick={() => handlePageChange(p)}
                                            >
                                                {p}
                                            </button>
                                        );
                                    }

                                    if (p === currentPage - 2 || p === currentPage + 2) {
                                        return (
                                            <span key={p} className={styles.pagerSeparator}>
                                                ...
                                            </span>
                                        );
                                    }

                                    return null;
                                })}

                                <button
                                    type="button"
                                    className={styles.imPageBtn}
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                >
                                    {">"}
                                </button>
                            </div>
                        )}
                        
                        <span className={styles.refreshTime}>
                            Last refreshed: {formatAdminDate(new Date())}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserKycPage;
